/**
 * Which server this Control Center runs on (ADR-018: one environment per deployment). The OT&E
 * server shows a permanent "Test environment (OT&E)" strip on every page and on the sign-in
 * page; the production server shows nothing. Read once from `GET /deployment`, which needs no
 * session.
 */

import { FlaskConical } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { api, type Deployment } from '../../api';

let cached: Deployment | null = null;
let loading = false;
const listeners = new Set<() => void>();

function load(): void {
  if (loading || cached) return;
  loading = true;
  api.deployment().then(
    (d) => {
      cached = d;
      loading = false;
      for (const l of listeners) l();
    },
    () => {
      // Unknown (server unreachable): show nothing rather than guess; asked again on the next render.
      loading = false;
    },
  );
}

/** The deployment, or null while it loads (or when the server cannot be reached). */
export function useDeployment(): Deployment | null {
  const value = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cached,
  );
  if (!value) load();
  return value;
}

export function TestSystemBanner({ className }: { className?: string }) {
  const { t } = useTranslation();
  const deployment = useDeployment();
  if (!deployment?.test_system) return null;
  return (
    <div role="note" aria-label={t('deployment.banner')} className={cn('flex items-center justify-center gap-2 border-b border-warning/40 bg-warning-soft px-3 py-1.5 text-center text-sm', className)}>
      <FlaskConical className="size-4 shrink-0 text-warning" aria-hidden="true" />
      <span className="font-semibold">{t('deployment.banner')}</span>
      <span className="hidden text-muted-foreground md:inline">{t('deployment.bannerDetail')}</span>
    </div>
  );
}
